/**
 * Базовый репозиторий.
 *
 * Инкапсулирует то, что иначе пришлось бы повторять в каждом модуле:
 * проставление служебных полей, мягкое удаление, инкремент ревизии и запись в
 * очередь синхронизации. Модули работают с предметной областью, а не с
 * механикой хранения.
 *
 * ```ts
 * const tasksRepo = createRepository<Task>('tasks', db.tasks);
 * await tasksRepo.create({ title: 'Позвонить врачу', estimateMin: 15, ... });
 * ```
 *
 * ### Почему `deletedAt` фильтруется в JS, а не индексом
 *
 * IndexedDB не индексирует `null`, поэтому «все неудалённые» нельзя выразить
 * запросом по индексу. Схема построена так, что сначала отрабатывает узкий
 * индексный запрос (по дате, статусу, владельцу), и лишь затем небольшой
 * результат фильтруется в памяти. Удалённых записей на порядки меньше живых,
 * так что стоимость фильтрации пренебрежима.
 */

import type { Collection, EntityTable, IndexableType, Table } from 'dexie';

import { createId } from '@/shared/lib/id';

import { db } from './database';
import type { BaseEntity, OutboxOperation } from './schema';
import { requireUserId } from './session-context';

/**
 * Монотонная метка времени для очереди синхронизации.
 *
 * `Date.now()` имеет разрешение в миллисекунду, а создание и последующее
 * изменение записи нередко укладываются в одну и ту же миллисекунду. При
 * сортировке очереди по такой метке порядок операций становится
 * непредсказуемым — на сервер может уйти `update` раньше `create`, и он
 * получит изменение несуществующей сущности.
 *
 * Счётчик гарантирует строгое возрастание: при совпадении времени значение
 * увеличивается на единицу. Расхождение с реальными часами не превышает числа
 * операций в миллисекунде и обнуляется при следующем тике.
 */
let lastSequence = 0;

function nextSequence(): number {
  const now = Date.now();
  lastSequence = now > lastSequence ? now : lastSequence + 1;
  return lastSequence;
}

/** Поля, которые репозиторий проставляет сам — их не передают при создании. */
type ManagedFields = 'id' | 'userId' | 'createdAt' | 'updatedAt' | 'deletedAt' | 'revision' | 'syncState';

export type CreateInput<T extends BaseEntity> = Omit<T, ManagedFields> & Partial<Pick<T, 'id'>>;
export type UpdateInput<T extends BaseEntity> = Partial<Omit<T, ManagedFields>>;

export interface Repository<T extends BaseEntity> {
  readonly table: EntityTable<T, 'id'>;

  create(input: CreateInput<T>): Promise<T>;
  createMany(inputs: CreateInput<T>[]): Promise<T[]>;
  update(id: string, patch: UpdateInput<T>): Promise<T | undefined>;
  /** Мягкое удаление: запись остаётся в базе с меткой `deletedAt`. */
  remove(id: string): Promise<void>;
  restore(id: string): Promise<void>;
  /** Необратимое удаление. Только для служебных сценариев (очистка мусора). */
  purge(id: string): Promise<void>;

  get(id: string): Promise<T | undefined>;
  /** Все активные записи текущего пользователя. */
  listAll(): Promise<T[]>;
  /** Запрос по индексу с автоматическим отсевом удалённых. */
  listBy(index: string, key: IndexableType): Promise<T[]>;
  count(): Promise<number>;
}

/** Отсекает мягко удалённые записи. */
export function alive<T extends BaseEntity>(records: T[]): T[] {
  return records.filter((record) => record.deletedAt === null);
}

/** То же для незавершённой коллекции Dexie — фильтр применяется до материализации. */
export function aliveOnly<T extends BaseEntity>(collection: Collection<T, string>): Collection<T, string> {
  return collection.filter((record) => record.deletedAt === null);
}

export function createRepository<T extends BaseEntity>(
  entityName: string,
  table: EntityTable<T, 'id'>,
): Repository<T> {
  /**
   * Дженерики Dexie построены вокруг конкретного типа записи и не выводятся
   * через параметр `T`, ограниченный лишь `BaseEntity`. Приведение делается
   * ровно один раз здесь; наружу репозиторий отдаёт строго типизированный API,
   * поэтому вызывающий код остаётся типобезопасным.
   */
  const store = table as unknown as Table<T, string>;

  /**
   * Регистрирует изменение в очереди синхронизации.
   *
   * Пишется в той же транзакции, что и само изменение: либо данные и запись
   * очереди появляются вместе, либо не появляется ничего. Иначе при сбое между
   * двумя операциями изменение навсегда осталось бы неотправленным.
   */
  async function enqueue(
    operation: OutboxOperation,
    entityId: string,
    payload: unknown,
    userId: string,
    revisions: { base: number; next: number },
  ): Promise<void> {
    await db.outbox.add({
      id: createId(),
      userId,
      entity: entityName,
      entityId,
      operation,
      payload,
      // Не `Date.now()`: очередь обязана сохранять порядок операций.
      createdAt: nextSequence(),
      baseRevision: revisions.base,
      revision: revisions.next,
      attempts: 0,
      lastAttemptAt: null,
      lastError: null,
    });
  }

  function buildEntity(input: CreateInput<T>, userId: string): T {
    const now = Date.now();
    return {
      ...(input as unknown as T),
      id: input.id ?? createId(),
      userId,
      createdAt: now,
      updatedAt: now,
      deletedAt: null,
      revision: 1,
      syncState: 'local',
    };
  }

  return {
    table,

    async create(input) {
      const userId = requireUserId();
      const entity = buildEntity(input, userId);

      await db.transaction('rw', store, db.outbox, async () => {
        await store.add(entity);
        // Создание строится «из ничего»: базовой версии у него нет.
        await enqueue('create', entity.id, entity, userId, { base: 0, next: entity.revision });
      });

      return entity;
    },

    async createMany(inputs) {
      if (inputs.length === 0) return [];
      const userId = requireUserId();
      const entities = inputs.map((input) => buildEntity(input, userId));

      await db.transaction('rw', store, db.outbox, async () => {
        await store.bulkAdd(entities);
        await Promise.all(
          entities.map((entity) =>
            enqueue('create', entity.id, entity, userId, { base: 0, next: entity.revision }),
          ),
        );
      });

      return entities;
    },

    async update(id, patch) {
      const userId = requireUserId();
      let result: T | undefined;

      await db.transaction('rw', store, db.outbox, async () => {
        const existing = await store.get(id);
        if (!existing) return;

        const updated: T = {
          ...existing,
          ...patch,
          updatedAt: Date.now(),
          revision: existing.revision + 1,
          // Ранее синхронизированная запись снова становится «ожидающей».
          syncState: existing.syncState === 'synced' ? 'pending' : existing.syncState,
        };

        await store.put(updated);
        await enqueue('update', id, patch, userId, {
          base: existing.revision,
          next: updated.revision,
        });
        result = updated;
      });

      return result;
    },

    async remove(id) {
      const userId = requireUserId();

      await db.transaction('rw', store, db.outbox, async () => {
        const existing = await store.get(id);
        if (!existing || existing.deletedAt !== null) return;

        await store.put({
          ...existing,
          deletedAt: Date.now(),
          updatedAt: Date.now(),
          revision: existing.revision + 1,
          syncState: existing.syncState === 'synced' ? 'pending' : existing.syncState,
        });
        await enqueue('delete', id, { id }, userId, {
          base: existing.revision,
          next: existing.revision + 1,
        });
      });
    },

    async restore(id) {
      const userId = requireUserId();

      await db.transaction('rw', store, db.outbox, async () => {
        const existing = await store.get(id);
        if (!existing) return;

        const restored: T = {
          ...existing,
          deletedAt: null,
          updatedAt: Date.now(),
          revision: existing.revision + 1,
          syncState: 'pending',
        };

        await store.put(restored);
        await enqueue('update', id, { deletedAt: null }, userId, {
          base: existing.revision,
          next: restored.revision,
        });
      });
    },

    async purge(id) {
      await store.delete(id);
    },

    async get(id) {
      const record = await store.get(id);
      return record?.deletedAt === null ? record : undefined;
    },

    async listAll() {
      const userId = requireUserId();
      return alive(await store.where('userId').equals(userId).toArray());
    },

    async listBy(index, key) {
      return alive(await store.where(index).equals(key).toArray());
    },

    async count() {
      const userId = requireUserId();
      return store.where('userId').equals(userId).count();
    },
  };
}
